import { useMutation, useQueryClient } from '@tanstack/react-query'
import { login, ssoTokenExchange, ssoLogout } from '@/api/auth'
import { useAuthStore } from '@/stores/useAuthStore'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/lib/queryKeys'

interface LoginRequest {
  email: string
  password: string
}

interface LoginResponse {
  access_token: string
  token_type: string
  expires_in?: number
}

interface SSOTokenExchangeRequest {
  code: string
  redirectUri: string
  codeVerifier: string
}

interface SSOLogoutResponse {
  logout_url?: string
}

/**
 * Hook for traditional login (email/password) — used by DebugLogin
 */
export function useLogin() {
  const setTokens = useAuthStore(state => state.setTokens)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ email, password }: LoginRequest): Promise<LoginResponse> =>
      login(email, password),

    onSuccess: data => {
      setTokens({ accessToken: data.access_token })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all })
    },
  })
}

/**
 * Hook for exchanging an OIDC authorization code for JWT tokens
 */
export function useSSOTokenExchange() {
  const setTokens = useAuthStore(state => state.setTokens)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      code,
      redirectUri,
      codeVerifier,
    }: SSOTokenExchangeRequest): Promise<LoginResponse> =>
      ssoTokenExchange(code, redirectUri, codeVerifier),

    onSuccess: data => {
      setTokens({ accessToken: data.access_token })
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboards.all })
      navigate('/')
    },
  })
}

/**
 * Hook for SSO logout — clears tokens and redirects to IBM Verify logout URL
 */
export function useSSOLogout() {
  const clearAuth = useAuthStore(state => state.clear)

  return useMutation({
    mutationFn: (): Promise<SSOLogoutResponse> => ssoLogout(),

    onSuccess: data => {
      clearAuth()
      if (data.logout_url) {
        window.location.href = data.logout_url
      } else {
        window.location.href = '/login'
      }
    },

    onError: () => {
      clearAuth()
      window.location.href = '/login'
    },
  })
}
